import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import {
    Button,
    Card,
    CardBody,
    CardTitle,
    Col,
    Row,
    Spinner,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone,
} from "react-bootstrap-table2-paginator";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import { KYBCreditTypes } from "src/modules/3rd-party-management/constants";
import DateUtils from "src/services/utils/DateUtils";
import { EMPTY_LIST, INTERNATIONAL_DATE_TIME_FORMAT } from "src/common/constants";
import FilteringComponent from "./FilteringComponent";

const DEFAULT_FILTERS = {
    pageIndex: 1,
    pageSize: 10,
    sortField: "createdAt",
    sortOrder: "desc",
    dateFrom: null,
    dateTo: null,
    supplierId: null,
};

const escapeCsvValue = (value) => {
    const stringValue = value == null ? "" : String(value);

    if (/[",\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
};

const buildUsageCsv = (rows = [], t, dateUtils) => {
    const headers = [
        t("ID"),
        t("Type"),
        t("Subject"),
        t("Amount"),
        t("Supplier"),
        t("Created By"),
        t("Date"),
    ];

    const body = rows.map((row) => {
        const typeTitle = KYBCreditTypes[row?.dataType]?.title;
        const typeLabel = typeTitle ? t(typeTitle) : (row?.dataType || "");
        const createdAt = row?.createdAt
            ? dateUtils.convertDateToDate(row.createdAt, INTERNATIONAL_DATE_TIME_FORMAT)
            : "";

        return [
            row?.id,
            typeLabel,
            row?.subject || "",
            row?.amount == null ? "" : row.amount,
            row?.supplier?.name || "",
            row?.createdBy?.fullName || "",
            createdAt,
        ].map(escapeCsvValue).join(",");
    });

    return [headers.map(escapeCsvValue).join(","), ...body].join("\n");
};

const CreditUsageTable = ({ t, provider, pool }) => {
    const dateUtils = new DateUtils();
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    const {
        data: usageData,
        isFetching: isFetchingUsage,
        isFetched: usageIsFetched,
    } = useQuery({
        queryKey: [
            "3rd-party-management-fetch-kyb-license-usage",
            provider,
            pool,
            filters,
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchKYBLicenseUsage({
                provider,
                ...(pool ? { pool } : {}),
                pageIndex: filters.pageIndex,
                pageSize: filters.pageSize,
                sortField: filters.sortField || "createdAt",
                sortOrder: (filters.sortOrder || "desc").toLowerCase(),
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                supplierId: filters.supplierId || undefined,
            });
        },
        enabled: Boolean(provider),
        cacheTime: 0,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        onError: (error) => {
            const message =
                error?.response?.data?.error
                || t("An error occurred while fetching usage data.");

            toast(message, {
                type: "error",
            });
        },
    });

    const rows = usageData?.result || [];
    const itemsCount = usageData?.itemsCount || 0;

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: true,
            style: { width: "80px" },
        },
        {
            dataField: "dataType",
            text: t("Type"),
            sort: true,
            formatter: (cellContent) => {
                const title = KYBCreditTypes[cellContent]?.title;
                return title ? t(title) : (cellContent || "-");
            },
        },
        {
            dataField: "subject",
            text: t("Subject"),
            sort: false,
            formatter: (cellContent) => cellContent || "-",
        },
        {
            dataField: "amount",
            text: t("Amount"),
            sort: true,
            formatter: (cellContent) =>
                cellContent == null ? "-" : Number(cellContent).toLocaleString(),
        },
        {
            dataField: "supplier",
            text: t("Supplier"),
            sort: true,
            formatter: (cellContent) => cellContent?.name || "-",
        },
        {
            dataField: "createdBy",
            text: t("Created By"),
            sort: false,
            formatter: (cellContent) => cellContent?.fullName || "-",
        },
        {
            dataField: "createdAt",
            text: t("Date"),
            sort: true,
            formatter: (cellContent) => {
                if (!cellContent) {
                    return "-";
                }

                return dateUtils.convertDateToDate(
                    cellContent,
                    INTERNATIONAL_DATE_TIME_FORMAT
                );
            },
        },
    ];

    const NoDataIndication = () => {
        if (usageIsFetched && !rows.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="mb-0 text-center">{t(EMPTY_LIST)}</p>
                </div>
            );
        }

        return <></>;
    };

    const handleTableChange = (type, options) => {
        const { page, sizePerPage, sortField, sortOrder } = options;

        if (type === "pagination") {
            setFilters((prev) => ({
                ...prev,
                pageIndex: page || 1,
                pageSize: sizePerPage,
            }));
            return;
        }

        if (type === "sort") {
            setFilters((prev) => ({
                ...prev,
                pageIndex: 1,
                sortField: sortField || "createdAt",
                sortOrder: (sortOrder || "desc").toLowerCase(),
            }));
        }
    };

    const handleFiltersChange = useCallback((newFilters) => {
        setFilters((prev) => ({
            ...prev,
            ...newFilters,
            pageIndex: 1,
        }));
    }, [ ]);

    const exportMutation = useMutation({
        mutationFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchKYBLicenseUsageExport({
                provider,
                ...(pool ? { pool } : {}),
                sortField: filters.sortField || "createdAt",
                sortOrder: (filters.sortOrder || "desc").toLowerCase(),
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                supplierId: filters.supplierId || undefined,
            });
        },
        onSuccess: (exportData) => {
            const exportRows = exportData?.result || [];

            if (!exportRows.length) {
                toast(t(EMPTY_LIST), {
                    type: "info",
                });
                return;
            }

            const csv = buildUsageCsv(exportRows, t, dateUtils);
            const blob = new Blob(["\uFEFF" + csv], {
                type: "text/csv;charset=utf-8;",
            });
            const fileName = [
                "license-usage",
                provider,
                pool || "kyb",
                dateUtils.convertDateToDate(new Date(), "YYYY-MM-DD"),
            ].filter(Boolean).join("-") + ".csv";

            saveAs(blob, fileName);
        },
        onError: (error) => {
            const message =
                error?.response?.data?.error
                || t("An error occurred while exporting usage data.");

            toast(message, {
                type: "error",
            });
        },
    });

    return (
        <Col sm="12" className="mt-3">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <CardTitle tag="h4" className="mb-0">
                            {t("License Usage")}
                        </CardTitle>

                        <div className="d-flex justify-content-end align-items-center">
                            <FilteringComponent
                                onChange={handleFiltersChange}
                            />
                            <Button
                                color="primary"
                                outline
                                className="ms-2"
                                disabled={exportMutation.isLoading || !provider}
                                onClick={() => exportMutation.mutate()}
                            >
                                {exportMutation.isLoading ? (
                                    <Spinner size="sm" className="me-1" />
                                ) : (
                                    <i className="ri-download-2-line align-middle me-1" />
                                )}
                                {t("Export")}
                            </Button>
                        </div>
                    </div>

                    <PaginationProvider
                        pagination={paginationFactory({
                            custom: true,
                            page: filters.pageIndex,
                            sizePerPage: filters.pageSize,
                            totalSize: itemsCount,
                            withFirstAndLast: true,
                            alwaysShowAllBtns: true,
                            prePageText: (
                                <span>
                                    <i className="ri-arrow-left-s-line" /> {t("Back")}
                                </span>
                            ),
                            nextPageText: (
                                <span>
                                    {t("Next")} <i className="ri-arrow-right-s-line" />
                                </span>
                            ),
                            prePageTitle: t("Pre page"),
                            firstPageTitle: t("First page"),
                            lastPageTitle: t("Last page"),
                            showTotal: false,
                            paginationTotalRenderer: (from, to, size) => (
                                <span className="react-bootstrap-table-pagination-total">
                                    {`${t("Total Items")} ${size}`}
                                </span>
                            ),
                            hideSizePerPage: false,
                            sizePerPageList: [
                                { text: "10", value: 10 },
                                { text: "25", value: 25 },
                                { text: "50", value: 50 },
                            ],
                        })}
                    >
                        {({ paginationProps, paginationTableProps }) => (
                            <>
                                <Row>
                                    <Col sm="12">
                                        <BootstrapTable
                                            loading={isFetchingUsage}
                                            remote={{
                                                pagination: true,
                                                filter: false,
                                                sort: true,
                                                cellEdit: false,
                                                search: false,
                                            }}
                                            overlay={overlayFactory({
                                                spinner: (
                                                    <Spinner
                                                        animation="border"
                                                        variant="primary"
                                                        size="md"
                                                    />
                                                ),
                                                text: `${t("Loading")}...`,
                                            })}
                                            keyField="id"
                                            responsive
                                            bordered={false}
                                            data={rows}
                                            striped
                                            columns={tableColumns}
                                            onTableChange={handleTableChange}
                                            wrapperClasses="table-responsive"
                                            classes="table tpdd-table"
                                            headerWrapperClasses="thead-light"
                                            noDataIndication={() => <NoDataIndication />}
                                            {...paginationTableProps}
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm="12">
                                        <div className="tpdd-pagination-style-1">
                                            <PaginationTotalStandalone {...paginationProps} />
                                            <div>
                                                <PaginationListStandalone {...paginationProps} />
                                                <SizePerPageDropdownStandalone {...paginationProps} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </PaginationProvider>
                </CardBody>
            </Card>
        </Col>
    );
};

export default withNamespaces()(CreditUsageTable);
